import React, { useState, useEffect, useMemo } from 'react';
import { ethers } from 'ethers';
import { formatAmount, parseAmount, ERC3643Info, getAllowance } from '../contracts/contract';
import './UserOperations.css';

interface UserOperationsProps {
  tokenInfo: ERC3643Info | null;
  onTransfer: (to: string, amount: string) => Promise<void>;
  onTransferFrom: (from: string, to: string, amount: string) => Promise<void>;
  onApprove: (spender: string, amount: string) => Promise<void>;
  loading: boolean;
  provider: ethers.Provider | null;
  account: string | null;
  contractAddress: string | null;
}

const UserOperations: React.FC<UserOperationsProps> = ({ tokenInfo, onTransfer, onTransferFrom, onApprove, loading, provider, account, contractAddress }) => {
  const [transferTo, setTransferTo] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferFromFrom, setTransferFromFrom] = useState('');
  const [transferFromTo, setTransferFromTo] = useState('');
  const [transferFromAmount, setTransferFromAmount] = useState('');
  const [approveSpender, setApproveSpender] = useState('');
  const [approveAmount, setApproveAmount] = useState('');
  const [allowance, setAllowance] = useState<string | null>(null);
  const [useTransferToMyAccount, setUseTransferToMyAccount] = useState(false);

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferTo || !transferAmount) return;
    
    try {
      await onTransfer(transferTo, transferAmount);
      setTransferTo('');
      setTransferAmount('');
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleTransferFrom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferFromFrom || !transferFromTo || !transferFromAmount) return;
    
    try {
      await onTransferFrom(transferFromFrom, transferFromTo, transferFromAmount);
      setTransferFromFrom('');
      setTransferFromTo('');
      setTransferFromAmount('');
      setUseTransferToMyAccount(false);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleApprove = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!approveSpender || !approveAmount) return;
    
    try {
      await onApprove(approveSpender, approveAmount);
      setApproveSpender('');
      setApproveAmount('');
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  // Fetch allowance when from address or account changes
  useEffect(() => {
    const fetchAllowance = async () => {
      if (!provider || !account || !contractAddress || !transferFromFrom || !tokenInfo) {
        setAllowance(null);
        return;
      }

      try {
        // Get allowance from transferFromFrom (owner) to account (spender)
        const allowanceAmount = await getAllowance(provider, contractAddress, transferFromFrom, account);
        setAllowance(allowanceAmount);
      } catch (error) {
        console.warn('Failed to fetch allowance:', error);
        setAllowance(null);
      }
    };

    fetchAllowance();
  }, [provider, account, contractAddress, transferFromFrom, tokenInfo]);

  // Check if allowance is sufficient for the transfer
  const isAllowanceInsufficient = useMemo(() => {
    if (!allowance || !transferFromAmount || !tokenInfo) {
      return false;
    }

    try {
      const parsedTransferAmount = parseAmount(transferFromAmount, tokenInfo.decimals);
      const allowanceBigInt = BigInt(allowance);
      const transferAmountBigInt = BigInt(parsedTransferAmount);
      
      return transferAmountBigInt > allowanceBigInt;
    } catch (error) {
      // If parsing fails, don't disable the button (let the contract handle validation)
      return false;
    }
  }, [allowance, transferFromAmount, tokenInfo]);

  // Handle checkbox change for "My account"
  const handleUseMyAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setUseTransferToMyAccount(checked);
    if (checked && account) {
      setTransferFromTo(account);
    } else {
      // Clear the field when unchecked
      setTransferFromTo('');
    }
  };

  // Sync transferFromTo with account when checkbox is checked and account changes
  useEffect(() => {
    if (useTransferToMyAccount && account) {
      setTransferFromTo(account);
    }
  }, [useTransferToMyAccount, account]);

  return (
    <div className="user-operations">
      <h3>Token Holder & Beneficiary/Spender Operations</h3>
      
      <div className="operations-grid">
        {/* Transfer Form */}
        <div className="operation-card">
        <h4>Transfer Tokens</h4>
        <form onSubmit={handleTransfer} className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="transfer-to">To Address:</label>
              <input
                id="transfer-to"
                type="text"
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="transfer-amount">Amount:</label>
              <input
                id="transfer-amount"
                type="number"
                step="0.000001"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                placeholder="0.0"
                required
              />
              {tokenInfo && (
                <small className="balance-info">
                  Your balance: {formatAmount(tokenInfo.balance, tokenInfo.decimals)} {tokenInfo.symbol}
                </small>
              )}
            </div>
          </div>
          <div className="button-container">
            <button type="submit" className="action-btn transfer-btn" disabled={loading}>
              Transfer
            </button>
          </div>
        </form>
      </div>

      {/* Approve Form */}
      <div className="operation-card">
        <h4>Approve Tokens</h4>
        <form onSubmit={handleApprove} className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="approve-spender">Spender Address:</label>
              <input
                id="approve-spender"
                type="text"
                value={approveSpender}
                onChange={(e) => setApproveSpender(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="approve-amount">Amount:</label>
              <input
                id="approve-amount"
                type="number"
                step="0.000001"
                value={approveAmount}
                onChange={(e) => setApproveAmount(e.target.value)}
                placeholder="0.0"
                required
              />
              {tokenInfo && (
                <small className="balance-info">
                  Your balance: {formatAmount(tokenInfo.balance, tokenInfo.decimals)} {tokenInfo.symbol}
                </small>
              )}
            </div>
          </div>
          <div className="button-container">
            <button type="submit" className="action-btn approve-btn" disabled={loading}>
              Approve
            </button>
          </div>
        </form>
      </div>

      {/* Transfer From Form */}
      <div className="operation-card">
        <h4>Transfer Tokens From</h4>
        <form onSubmit={handleTransferFrom} className="operation-form">
          <div className="form-fields">
            <div className="form-group">
              <label htmlFor="transfer-from-from">From Address:</label>
              <input
                id="transfer-from-from"
                type="text"
                value={transferFromFrom}
                onChange={(e) => setTransferFromFrom(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="transfer-from-to">
                To Address:
                <span style={{ marginLeft: '10px', fontWeight: 'normal' }}>
                  <input
                    type="checkbox"
                    id="use-my-account-checkbox"
                    checked={useTransferToMyAccount}
                    onChange={handleUseMyAccountChange}
                    style={{ marginRight: '5px' }}
                  />
                  <label htmlFor="use-my-account-checkbox" style={{ cursor: 'pointer', fontSize: '14px' }}>
                    My account
                  </label>
                </span>
              </label>
              <input
                id="transfer-from-to"
                type="text"
                value={transferFromTo}
                onChange={(e) => setTransferFromTo(e.target.value)}
                placeholder="0x..."
                required
                disabled={useTransferToMyAccount}
              />
            </div>
            <div className="form-group">
              <label htmlFor="transfer-from-amount">Amount:</label>
              <input
                id="transfer-from-amount"
                type="number"
                step="0.000001"
                value={transferFromAmount}
                onChange={(e) => setTransferFromAmount(e.target.value)}
                placeholder="0.0"
                required
              />
              {tokenInfo && (
                <small className="balance-info">
                  {allowance !== null ? (
                    <>
                      Your allowance: {formatAmount(allowance, tokenInfo.decimals)} {tokenInfo.symbol}
                    </>
                  ) : (
                    <>
                      Enter a from address to see your allowance
                    </>
                  )}
                </small>
              )}
            </div>
          </div>
          <div className="button-container">
            <button 
              type="submit" 
              className="action-btn transfer-from-btn" 
              disabled={loading || isAllowanceInsufficient}
            >
              {isAllowanceInsufficient 
                ? 'Allowance Too Low' 
                : useTransferToMyAccount 
                  ? 'Receive' 
                  : 'Transfer'}
            </button>
          </div>
        </form>
      </div>

      </div>
    </div>
  );
};

export default UserOperations;
